import React from 'react'
import classNames from 'classnames'
import { ZodError } from 'zod'
import { Button, ButtonProps, LinkButton, LinkButtonProps } from '@/atoms/Button'
import { CaretLeftIcon } from '@/atoms/Icons/CaretLeftIcon'
import { CircleInfoIcon } from '@/atoms/Icons/CircleInfoIcon'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { CopyIcon } from '@/atoms/Icons/CopyIcon'
import { WarningCircleIcon } from '@/atoms/Icons/WarningCircleIcon'
import { AsDiv, AsSpan, CaptionMD, EyebrowMD, LabelMD, ParagraphSM } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { Tooltip } from '@/molecules/Tooltip'
import { formatPrice } from '@/services/PriceFormatter'
import { useLocale } from '@/utils/LocaleUtil'
import { useTranslate } from '@/utils/translate/translate-client'
import { GuildGiftContext } from './state'
import { GuildGiftScreen } from './state'

export function BackButton(props: { screen: GuildGiftScreen; label: string }) {
  const { dispatch } = React.useContext(GuildGiftContext)
  return (
    <button className="flex items-center gap-2" onClick={() => dispatch({ type: 'transition', screen: props.screen })}>
      <CaretLeftIcon size={24} />
      <EyebrowMD as={AsSpan} color="core-gray-400" weight="semibold">
        {props.label}
      </EyebrowMD>
    </button>
  )
}

export function CtaButton({ className, ...rest }: Partial<ButtonProps>) {
  return (
    <Button
      {...rest}
      variant="white"
      className={classNames('!border-[#FDA21E] !bg-[#FDA21E] w-full md:w-fit px-4 py-2 whitespace-nowrap', className)}
    ></Button>
  )
}

export function CtaLinkButton({
  className,
  children,
  ...rest
}: Omit<LinkButtonProps, 'variant' | 'children'> & {
  href: string
  children: React.ReactNode
}) {
  return (
    <LinkButton
      {...rest}
      variant="white"
      className={classNames('inline-flex !border-[#FDA21E] !bg-[#FDA21E] px-4 py-2', className)}
    >
      {children}
    </LinkButton>
  )
}

export function ValidationMessage(props: { isVisible?: boolean; name: string; error?: ZodError }) {
  const issue = props.error?.issues.find((issue) => issue.path[0] === props.name)
  return (
    <div className="mt-1 flex min-h-7 items-center gap-2 text-error-400 md:pt-2">
      {props.isVisible && issue ? (
        <>
          <WarningCircleIcon color="error-400" size={16} /> {issue.message}
        </>
      ) : (
        ' '
      )}
    </div>
  )
}
interface LabelProps extends React.PropsWithChildren<React.ComponentProps<'input'>> {
  label: React.ReactNode
}
export function Label(props: LabelProps) {
  return (
    <label className="w-full text-core-gray-400">
      <CaptionMD as={AsSpan} className="mb-1">
        {props.label}
      </CaptionMD>
      {props.children}
    </label>
  )
}

export function RadioLabel(
  props: React.PropsWithChildren<{
    label: React.ReactNode
  }>,
) {
  return (
    <label className="relative flex cursor-pointer items-center gap-2 pl-4 text-core-gray-400 before:absolute before:left-0 before:top-[-6px] before:inline-block before:size-6 before:shrink-0 before:rounded-full before:border before:border-core-gray-800 before:bg-core-gray-900 after:absolute after:left-1 after:top-[-2px] after:size-4 after:shrink-0 after:rounded-full focus-within:before:ring has-[:checked]:before:border-core-gray-500 has-[:checked]:after:bg-oxide">
      {props.children}
      <LabelMD weight="semibold" color="white">
        {props.label}
      </LabelMD>
    </label>
  )
}

export function Radio(props: React.ComponentProps<'input'>) {
  return (
    <>
      <input className="opacity-0" {...props} type="radio" />
    </>
  )
}

export function DateInput({ className, value, ...rest }: React.ComponentProps<'input'>) {
  return (
    <Input
      type="date"
      className={classNames(
        'relative [color-scheme:dark] [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3',
        className,
      )}
      value={formatValue(value)}
      {...rest}
    />
  )

  function formatValue(value: string | number | readonly string[] | undefined) {
    if (!value) return ''

    const [yyyy, mm, dd] = String(value).split('-')
    return `${yyyy}-${mm}-${dd}`
  }
}

export function Input({ className, ...rest }: React.ComponentProps<'input'>) {
  return (
    <input
      className={classNames(
        'flex h-12 w-full items-center rounded-lg border border-core-gray-800 bg-white px-4 text-core-gray-950',
        className,
      )}
      {...rest}
    />
  )
}

export function TextArea(props: React.ComponentProps<'textarea'>) {
  return (
    <textarea
      className="flex min-h-20 w-full items-center rounded-lg border border-core-gray-800 bg-white p-4 text-core-gray-950"
      {...props}
    ></textarea>
  )
}

export function HelpText(props: { text: string }) {
  return <div className="mt-2 text-core-gray-500">{props.text}</div>
}

export function PromoPrice({ normalPrice, promoPrice }: { normalPrice: number; promoPrice: number }) {
  const { locale } = useLocale()
  return (
    <>
      <span className="pointer-events-none relative text-core-gray-600">
        <CloseIcon
          size={999}
          color="warm-01"
          className="absolute left-1/2 top-0 h-full -translate-x-1/2 rotate-[12deg]"
        />
        {formatPrice(normalPrice, { locale, includeDecimals: false, currency: 'USD' })}
      </span>
      <span className="inline-block text-inherit">
        {formatPrice(promoPrice, { locale, includeDecimals: false, currency: 'USD' })}
      </span>
    </>
  )
}

export function CopyLinkButton({
  inviteId,
  ...rest
}: { inviteId: string } & Partial<React.ComponentProps<typeof Button>>) {
  const { t } = useTranslate('guild')
  const [isCopied, setIsCopied] = React.useState(false)
  const url =
    window.location.protocol +
    '//' +
    window.location.host +
    paths.guild.gift.invite(inviteId) +
    '?' +
    new URLSearchParams({ source: 'copy' })

  function handleCopyClick() {
    setIsCopied(true)
    navigator.clipboard.writeText(url)
    setTimeout(() => {
      setIsCopied(false)
    }, 2000)
  }

  return (
    <div className="flex cursor-pointer items-center justify-center gap-2" onClick={handleCopyClick}>
      <div className="relative">
        <div className="flex items-center gap-2">
          <Button variant="gray" className="inline-flex items-center gap-2" {...rest}>
            {t('copyGiftLink', 'Copy Gift Link')}
            <CopyIcon color="core-gray-700" size={20} />
          </Button>
          <Tooltip
            content={t(
              'weWillAutomaticallySend',
              "We will automatically gift in an email, but sometimes it ends up in the spam folder. If your intended recipient doesn't see it, maybe you'd also like to send this link when the time is right.",
            )}
          >
            <CircleInfoIcon size={24} color="core-gray-700" />
          </Tooltip>
        </div>
        {isCopied && (
          <ParagraphSM
            className="absolute -right-2 top-0 translate-x-full translate-y-2"
            weight="bold"
            color="success"
            as={AsDiv}
          >
            {t('copied', 'Copied!')}
          </ParagraphSM>
        )}
      </div>
    </div>
  )
}
